import PropTypes from "prop-types";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Button, CircularProgress, Grid, InputAdornment } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useState } from "react";
import ChangePassword from "./change-password";
import { UserService } from "../../../../assets/_services/user-service";
import { openSuccessDialog } from "../../../../utils/ui-components/pop-ups/SuccessDialog";
import MultipleSelectCheckmarksWrapper from "../../../../utils/ui-components/FormsUI/MultipleSelectCheckmarks";
import { gridSpacing } from "../../../../store/constants";
import { UserAddValues } from "./user-list";
import TextFieldWrapper from "../../../../utils/ui-components/FormsUI/TextField";
import SelectWrapper from "../../../../utils/ui-components/FormsUI/Select";
import SwitchWrapper from "../../../../utils/ui-components/FormsUI/Switch";
import { ChannelService } from "../../../../assets/_services/channel-service";

UpdateParentUser.propTypes = {
  initialItem: PropTypes.object.isRequired,
  setOpen: PropTypes.func.isRequired,
  fetchData: PropTypes.func,
  initialData: PropTypes.object,
};

function UpdateParentUser({ initialItem, setOpen, fetchData, initialData }: any) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [channels, setChannels] = useState<{ value: number | string; label: string }[]>([]);
  // console.log(initialItem);

  useEffect(() => {
    getAllFlashAndSmsChannels(initialItem.rateCardId);
  }, []);

  const getAllFlashAndSmsChannels = (id: number | string) => {
    const values = {
      rateCardId: id, // ratecard id
      channelType: [1, 2], // get only flash (1) and bulk sms (2) channels
    };
    ChannelService.getRateCardChannelList(values).then((response) => {
      if (response.isSuccess) {
        setChannels(
          response.data.data.map((item: any) => {
            return {
              label: `${item.channel_name} ( ${
                Number(item.channel_type) === 0
                  ? "API"
                  : Number(item.channel_type) === 1
                  ? "Flash"
                  : Number(item.channel_type) === 2
                  ? "Bulk Sms"
                  : "-"
              } )`,
              value: item.id,
            };
          })
        );
      }
    });
  };

  return (
    <Grid container justifyContent={"center"}>
      <Grid item xs={12}>
        <Formik
          validateOnMount={true}
          initialValues={initialItem}
          enableReinitialize
          validationSchema={Yup.object().shape({
            companyName: Yup.string().required("Representative name is required"),
            firstApprover: Yup.string().required("First approver is required"),
            secondApprover: Yup.string().notRequired(),
            companyContact: Yup.string()
              .matches(/^[1-9]\d{8}$/, "Invalid contact numbers pattern detected")
              .required("Please enter the contact number"),
            username: Yup.string()
              .required("Username is required")
              .matches(/^[a-zA-Z0-9_]*$/, "Username can contain only letters, numbers, and underscores"),
            status: Yup.boolean().notRequired(),
            rateCardId: Yup.number().required("Rate Card is required"),
            assignChannelList: Yup.array()
              .of(
                Yup.object().shape({
                  value: Yup.number().required("Channel value is required"),
                })
              )
              .required("Channel is required")
              .min(1, "At least one channel is required"),
          })}
          onSubmit={(values: UserAddValues, { setSubmitting, resetForm }) => {
            setIsLoading(true);
            let formData = {
              userId: values.id,
              companyName: values.companyName,
              companyContact: values.companyContact,
              userName: values.username,
              status: values.status ? 1 : 0,
              rateCardId: values.rateCardId,
              assignChannelList: values.assignChannelList.map((item: any) => item.value),
              firstLevelApproval: values.firstApprover,
              secondLevelApproval: values.allowSecondApprover ? values.secondApprover : null,
            };
            UserService.updateParnetUser(formData).then((response) => {
              if (response.isSuccess) {
                openSuccessDialog("Success", `${values.companyName} updated successfully. Ready to use`);
                fetchData();
                setIsLoading(false);
                resetForm({ values });
              } else {
                setSubmitting(false);
                setIsLoading(false);
              }
            });
          }}
        >
          {({ values, dirty, isSubmitting, isValid, setFieldValue, errors }) => (
            <Form>
              <Grid container columnSpacing={gridSpacing}>
                <Grid item xs={12} md={12}>
                  <TextFieldWrapper required label="Company Name" name="companyName" type="text" />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectWrapper
                    label="Rate Card"
                    name="rateCardId"
                    options={initialData.rateCards}
                    customHandleChange={(e: any) => {
                      setFieldValue("rateCardId", e.target.value);
                      setFieldValue("assignChannelList", []);
                      getAllFlashAndSmsChannels(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <MultipleSelectCheckmarksWrapper
                    name="assignChannelList"
                    label="Assign Channels"
                    options={channels}
                    disabled={!values.rateCardId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldWrapper
                    required
                    placeholder="Company Contact Number"
                    name="companyContact"
                    type="tel"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+94 |</InputAdornment>,
                    }}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let mobileNumber = e.target.value;
                      if (mobileNumber.length === 10 && mobileNumber.startsWith("0")) {
                        setFieldValue("companyContact", mobileNumber.slice(1));
                      } else if (mobileNumber.length === 11 && mobileNumber.startsWith("94")) {
                        setFieldValue("companyContact", mobileNumber.slice(2));
                      } else {
                        setFieldValue("companyContact", mobileNumber);
                      }
                    }}
                    mataprops={initialItem.id ? errors.companyContact : null}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectWrapper
                    label="First Approver"
                    name="firstApprover"
                    options={initialData.firstApprovers}
                    customHandleChange={(e: any) => {}}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SwitchWrapper
                    name="allowSecondApprover"
                    label={values.allowSecondApprover ? "Enable Second Level Approver" : "Disabled Second Level Approver"}
                    style={{ co: "green" }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectWrapper
                    label="Second Approver"
                    name="secondApprover"
                    options={initialData.secondApprovers}
                    customHandleChange={(e: any) => {}}
                    disabled={!values.allowSecondApprover}
                    required={values.allowSecondApprover}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldWrapper
                    type={"text"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                    name="username"
                    placeholder="Username"
                    required
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <SwitchWrapper name="status" label={values.status ? "ACTIVE" : "INACTIVE"} style={{ co: "green" }} />
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      py: 2,
                    }}
                  >
                    <Button
                      color="primary"
                      variant="contained"
                      type="submit"
                      // onClick={() => {
                      //   console.log(values);
                      // }}
                      disabled={!dirty || isSubmitting || !isValid}
                      startIcon={isLoading && <CircularProgress size={"20px"} sx={{ color: "gray" }} />}
                    >
                      Update User
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>

      {/* <Grid item xs={12}>
        <Formik
          initialValues={initialItem}
          enableReinitialize
          validationSchema={Yup.object().shape({
            assignChannelList: Yup.array()
              .of(
                Yup.object().shape({
                  value: Yup.number().required("Channel value is required"),
                })
              )
              .required("Channel is required")
              .min(1, "At least one channel is required"),
          })}
          onSubmit={(values: any, { setSubmitting, resetForm }) => {
            setIsLoadingChannels(true);
            let formData = {
              ownerId: values?.id,
              assignChannelList: values.assignChannelList.map((item: any) => item.value),
            };
            UserService.updateParnetAssignChannels(formData).then((response) => {
              if (response.isSuccess) {
                openSuccessDialog("Success", response.data.comment);
                fetchData();
                setIsLoadingChannels(false);
                resetForm({ values });
              } else {
                setSubmitting(false);
                setIsLoadingChannels(false);
              }
            });
          }}
        >
          {({ values, dirty, isSubmitting, isValid, setFieldValue }) => (
            <Form>
              <Grid container columnSpacing={gridSpacing}>
                <Grid item xs={12}>
                  <MultipleSelectCheckmarksWrapper name="assignChannelList" label="Assign Channels" options={channels} />
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      py: 2,
                    }}
                  >
                    <Button
                      color="primary"
                      variant="contained"
                      type="submit"
                      startIcon={
                        isLoadingChannels && (
                          <CircularProgress
                            size={"20px"}
                            sx={{
                              color: "gray",
                            }}
                          />
                        )
                      }
                      disabled={!dirty || isSubmitting || !isValid}
                    >
                      Update Assign Channels
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid> */}

      <Grid item xs={12}>
        <ChangePassword
          fetchData={fetchData}
          setOpen={setOpen}
          initialItem={{
            password: "",
            confirmPassword: "",
            userId: initialItem.id,
          }}
        />
      </Grid>
    </Grid>
  );
}

export default UpdateParentUser;
